/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import ExcelJS from 'exceljs'
import {saveAs} from 'file-saver'
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import Dates from './Dates'
import { toast } from 'utils/toastUtils'

const configurePdfMakeFonts = () => {
    const pdfMakeAny = pdfMake as any
    const pdfFontsAny = pdfFonts as any

    const vfs = pdfFontsAny?.pdfMake?.vfs || pdfFontsAny?.vfs || pdfFontsAny
    if (!vfs || typeof vfs !== 'object') {
        return
    }

    if (typeof pdfMakeAny.addVirtualFileSystem === 'function') {
        pdfMakeAny.addVirtualFileSystem(vfs)
        return
    }

    pdfMakeAny.vfs = {
        ...(pdfMakeAny.vfs || {}),
        ...vfs,
    }
}

configurePdfMakeFonts()

export const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
}

export const viewBlob = (blob: Blob, _filename: string = 'document') => {
    const url = window.URL.createObjectURL(blob)
    window.open(url, '_blank')
}

export const base64ToBlob = (base64: string, type = 'application/pdf') => {
    const bin = atob(base64)
    const len = bin.length
    const arr = new Uint8Array(len)
    for (let i = 0; i < len; i++) {
        arr[i] = bin.charCodeAt(i)
    }
    return new Blob([arr], { type })
}

export const downloadBase64File = (
    base64: string,
    filename: string,
    type = 'application/pdf'
) => {
    const blob = base64ToBlob(base64, type)
    downloadBlob(blob, filename)
}

/**
 * Export table data to XLSX using exceljs
 */
export const exportToXlsxFromTable = async (table: any, filename: string) => {
    const workbook = new ExcelJS.Workbook()
    const worksheet = workbook.addWorksheet('Sheet1')

    const headerGroups = table.getHeaderGroups()
    const rows = table.getFilteredRowModel().rows

    // Extract headers
    const headers = headerGroups[0].headers.map((header: any) => {
        const def = header.column.columnDef
        if (typeof def.header === 'string') return def.header
        if (def.headerLabel) return def.headerLabel // Custom property if exists
        return header.id.charAt(0).toUpperCase() + header.id.slice(1)
    })

    const headerRow = worksheet.addRow(headers)

    // Style header row
    headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } }
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF4F46E5' }, // Indigo-600
        }
        cell.alignment = { vertical: 'middle', horizontal: 'center' }
        cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
        }
    })

    // Add data rows
    rows.forEach((row: any, index: number) => {
        const rowData = row.getVisibleCells().map((cell: any) => {
            return cell.getValue()
        })
        const addedRow = worksheet.addRow(rowData)

        // Alternating row background
        if (index % 2 === 1) {
            addedRow.eachCell((cell) => {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFF9FAFB' }, // Gray-50
                }
            })
        }

        // Add borders to cells
        addedRow.eachCell((cell) => {
            cell.border = {
                top: { style: 'thin', color: { argb: 'FFE5E7EB' } },
                left: { style: 'thin', color: { argb: 'FFE5E7EB' } },
                bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
                right: { style: 'thin', color: { argb: 'FFE5E7EB' } },
            }
        })
    })

    // Auto-fit columns
    worksheet.columns.forEach((column) => {
        let maxLength = 0
        column?.eachCell?.({ includeEmpty: true }, (cell) => {
            const columnLength = cell.value ? cell.value.toString().length : 10
            if (columnLength > maxLength) {
                maxLength = columnLength
            }
        })
        column.width = maxLength < 10 ? 10 : maxLength + 2
    })

    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    saveAs(blob, `${filename}.xlsx`)
}

/**
 * PDF generation utility with branding and direct context call
 */
export const exportToPdfFromTable = (table: any, title: string) => {
    const rows = table.getFilteredRowModel().rows
    const headerGroups = table.getHeaderGroups()

    // Filter and prepare headers - exclude "actions" and "selection"
    const relevantHeaders = headerGroups[0].headers.filter((header: any) => {
        const id = header.id.toLowerCase()
        return id !== 'actions' && id !== 'selection'
    })

    // Helper to allow wrapping of long strings without spaces
    const wrapLongString = (str: any) => {
        if (typeof str !== 'string' || str.includes(' ')) return str
        return str.replace(/(.{15})/g, '$1\u200B')
    }

    const headers = relevantHeaders.map((header: any) => {
        const def = header.column.columnDef
        if (typeof def.header === 'string') return def.header
        // fallback to startCase of the id
        return header.id
            .replace(/_/g, ' ')
            .replace(/([A-Z])/g, ' $1')
            .trim()
            .split(' ')
            .map(
                (word: string) =>
                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            )
            .join(' ')
    })

    // Smart column width calculation
    const widths = relevantHeaders.map((header: any) => {
        const id = header.id.toLowerCase()
        // Known small columns
        if (
            id === 'id' ||
            id.includes('status') ||
            id.includes('category') ||
            id === 'qty' ||
            id === 'count'
        ) {
            return 'auto'
        }
        // Fallback to star for content columns
        return '*'
    })

    // Ensure at least one '*' to stretch the table to 100% width
    if (widths.length > 0 && !widths.includes('*')) {
        widths[widths.length - 1] = '*'
    }

    const body = [
        headers.map((h: string) => ({
            text: h,
            style: 'tableHeader',
        })),
    ]

    rows.forEach((row: any) => {
        const visibleCells = row.getVisibleCells().filter((cell: any) => {
            const id = cell.column.id.toLowerCase()
            return id !== 'actions' && id !== 'selection'
        })

        body.push(
            visibleCells.map((cell: any) => {
                let val = cell.getValue()
                const id = cell.column.id.toLowerCase()

                // Special handling for contact info to put email and phone on separate lines
                if (
                    (id === 'contact' || id === 'email' || id === 'phone') &&
                    typeof val === 'string'
                ) {
                    // If it contains both, they are usually separated by space or other char
                    // We try to find common patterns or if they are passed as separate lines in some cells
                    // But here we just ensure if we have multiple pieces of info, they get split.
                    // For now, let's assume if it contains both email and phone, we want them split.
                    // A common pattern might be "email phone" or "email \n phone"
                    // We normalize to \n
                    val = val.replace(/\s+([+0-9])/g, '\n$1')
                }

                // Wrap long strings that don't have spaces (like tokens or long IDs)
                const text =
                    val !== null && val !== undefined
                        ? wrapLongString(String(val))
                        : ''

                return {
                    text,
                    style: 'tableCell',
                }
            })
        )
    })

    const docDefinition: any = {
        pageOrientation: 'landscape',
        pageSize: 'A4',
        pageMargins: [30, 80, 30, 60],
        header: (currentPage: number, pageCount: number) => {
            return {
                margin: [30, 20, 30, 0],
                columns: [
                    {
                        text: 'ArboPark Action & Fun Center',
                        style: 'brandTitle',
                        width: '*',
                    },
                    {
                        stack: [
                            { text: title, style: 'headerTitle' },
                            {
                                text: `Page ${currentPage} of ${pageCount}`,
                                style: 'headerSubTitle',
                            },
                        ],
                        alignment: 'right',
                        width: 'auto',
                    },
                ],
            }
        },
        footer: (_currentPage: number, _pageCount: number) => {
            return {
                margin: [30, 20, 30, 0],
                columns: [
                    {
                        text: `Generated on ${Dates.ymd(new Date())} ${new Date().toLocaleTimeString()}`,
                        style: 'footerText',
                    },
                    {
                        text: 'ArboPark Action & Fun Center',
                        style: 'footerText',
                        alignment: 'right',
                    },
                ],
            }
        },
        content: [
            {
                table: {
                    headerRows: 1,
                    widths: widths,
                    body: body,
                },
                layout: {
                    hLineWidth: (i: number, node: any) =>
                        i === 0 || i === node.table.body.length ? 1 : 0.5,
                    vLineWidth: () => 0.5,
                    hLineColor: (i: number, node: any) =>
                        i === 0 || i === node.table.body.length
                            ? '#1E293B'
                            : '#E2E8F0',
                    vLineColor: () => '#E2E8F0',
                    paddingLeft: () => 8,
                    paddingRight: () => 8,
                    paddingTop: () => 6,
                    paddingBottom: () => 6,
                    fillColor: (rowIndex: number) => {
                        if (rowIndex === 0) return '#1E293B' // Slate-800
                        return rowIndex % 2 === 0 ? '#F8FAFC' : null // Slate-50 alternating
                    },
                },
            },
        ],
        styles: {
            brandTitle: {
                fontSize: 20,
                bold: true,
                color: '#BE185D', // Pink-700 (ArboPark-ish red/pink)
                margin: [0, 5, 0, 0],
            },
            headerTitle: {
                fontSize: 18,
                bold: true,
                color: '#0F172A',
                margin: [0, 0, 0, 2],
            },
            headerSubTitle: {
                fontSize: 10,
                color: '#64748B',
            },
            footerText: {
                fontSize: 9,
                color: '#94A3B8',
            },
            tableHeader: {
                bold: true,
                fontSize: 10,
                color: 'white',
                alignment: 'left',
            },
            tableCell: {
                fontSize: 9,
                color: '#334155',
            },
        },
        defaultStyle: {
            // font: 'Roboto',
        },
    }

    const pdfMakeAny = pdfMake as any

    // Ensure we call createPdf on the correct object to preserve "this" context
    if (typeof pdfMakeAny.createPdf === 'function') {
        pdfMakeAny.createPdf(docDefinition).download(`${title}.pdf`)
    } else if (
        pdfMakeAny.default &&
        typeof pdfMakeAny.default.createPdf === 'function'
    ) {
        pdfMakeAny.default.createPdf(docDefinition).download(`${title}.pdf`)
    } else {
        console.error('pdfMake.createPdf is not available', pdfMakeAny)
    }
}

type ApiErrorBody = {
    message?: string
    errors?: Record<string, string[] | string>
    data?: {
        messages?: Record<string, string[] | string>
    }
}

const normalizeErrorMessages = (value: unknown): string[] => {
    if (Array.isArray(value)) {
        return value.map(String).map((item) => item.trim()).filter(Boolean)
    }

    if (typeof value === 'string' && value.trim()) {
        return [value.trim()]
    }

    return []
}

const getApiErrorBody = (error: unknown): ApiErrorBody | undefined => {
    if (!error || typeof error !== 'object') {
        return undefined
    }

    const candidate = error as Record<string, unknown>
    const nestedData = candidate.data

    if (nestedData && typeof nestedData === 'object' && !Array.isArray(nestedData)) {
        const body = nestedData as ApiErrorBody
        if (body.message || body.errors || body.data) {
            return body
        }
    }

    if (typeof candidate.message === 'string' || candidate.errors) {
        return candidate as ApiErrorBody
    }

    return undefined
}

export const getExportErrorMessage = (error: unknown, fallbackMessage = ''): string => {
    const body = getApiErrorBody(error)
    const messages: string[] = []

    if (body?.errors && typeof body.errors === 'object') {
        Object.values(body.errors).forEach((entry) => {
            messages.push(...normalizeErrorMessages(entry))
        })
    }

    if (body?.data?.messages && typeof body.data.messages === 'object') {
        Object.values(body.data.messages).forEach((entry) => {
            messages.push(...normalizeErrorMessages(entry))
        })
    }

    if (typeof body?.message === 'string' && body.message.trim()) {
        messages.unshift(body.message.trim())
    }

    const uniqueMessages = [...new Set(messages.filter(Boolean))]
    if (uniqueMessages.length > 0) {
        return uniqueMessages.join(', ')
    }

    if (error instanceof Error && error.message.trim()) {
        const message = error.message.trim()
        if (message !== 'Network Error') {
            return message
        }
    }

    const status = (error as { status?: number })?.status
    if (status) {
        return fallbackMessage ? `${fallbackMessage} (HTTP ${status})` : `HTTP ${status}`
    }

    return fallbackMessage
}

export const showExportError = (
    error: unknown,
    fallbackMessage: string,
    context?: string
): void => {
    const label = context ? `${context} export` : 'Export'
    console.error(`${label} failed`, error)

    const detail = getExportErrorMessage(error, '')
    const message =
        detail && detail !== fallbackMessage
            ? `${fallbackMessage}: ${detail}`
            : fallbackMessage || detail || 'Export failed'

    toast.error(message)
}
